import React, { useMemo } from 'react';
import * as gluestack from '@gluestack-ui/themed';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Icon,
  Button,
  ButtonText,
  Spinner,
  useToken,
  Pressable,
} from '@gluestack-ui/themed';
import {
  Target, ArrowLeft,
} from 'lucide-react-native';
import Animated, {
  useSharedValue,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  interpolate,
  interpolateColor,
  Extrapolation,
} from 'react-native-reanimated';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';
import { Platform, ViewStyle } from 'react-native';
import { Module, Task, Level } from '@/student/types';
import { isEventDeliveredTask } from '@/common/utils/tasks/isEventDeliveredTask';
import { usePacingData, useGroupedTasks } from '../hooks/useJourneyPacing';
import { PacingHeader } from './PacingHeader';
import { TimelineTask } from './TimelineTask';
import type { TaskStatus } from './TimelineTask';
import { getColorForModule } from '../../../common/utils/getColorForModule';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FALLBACK_IMAGE = require('@assets/img/fallback-module-image.jpg');

const AnimatedBox = Animated.createAnimatedComponent(Box);

interface JourneyTimelineProps {
  tasks: Task[];
  levels: Level[];
  selectedTaskId: string | null;
  onSelectTask: (task: Task) => void;
  allTasks?: Task[];
  module: Module;
  targetWeeks: number; // Now controlled by parent
  onUpdateWeeks: (weeks: number) => void;
  allTasksCompleted?: boolean;
  completedCount: number;
  totalTasks: number;
  onConfirmModule?: () => void;
  isSubmitting?: boolean;
  onBack: () => void;
  isMobile: boolean;
}

/**
 * Get status for a task
 */
const getTaskStatus = (task: Task, isFirstIncomplete: boolean): TaskStatus => {
  if (task.submission?.state === 'marked_as_done') return 'completed';
  if (isEventDeliveredTask(task) && task.eventConfig?.locked) return 'locked';
  if (isFirstIncomplete) return 'current';
  return 'upcoming';
};

const heroHeight = 320;
const SCROLL_VIEW_CONTENT_STYLE = { paddingBottom: 100 };

/**
 * JourneyTimeline
 * ---------------
 * Main timeline view showing the student's progress through a module.
 */
export const JourneyTimeline: React.FC<JourneyTimelineProps> = ({
  tasks,
  levels,
  selectedTaskId,
  onSelectTask,
  allTasks,
  module,
  targetWeeks,
  onUpdateWeeks,
  allTasksCompleted = false,
  completedCount,
  totalTasks,
  onConfirmModule,
  isSubmitting = false,
  onBack,
  isMobile,
}) => {
  const pacing = usePacingData(tasks, targetWeeks);
  const { levelGroups, isPacingDisabled } = useGroupedTasks(tasks, levels, targetWeeks);

  const levelOrder = useMemo(
    () => module.metadata?.levels?.map((l) => l.id) || levels.map((l) => l.id) || [],
    [module, levels],
  );

  const effectiveAllTasks = allTasks || tasks;
  const firstIncompleteId = useMemo(() => {
    const firstIncomplete = effectiveAllTasks.find((t) => t.submission?.state !== 'marked_as_done');
    return firstIncomplete?.id || null;
  }, [effectiveAllTasks]);

  const white = useToken('colors', 'white');
  // @ts-expect-error - neutral200 is a semantic token
  const neutral200 = useToken('colors', 'neutral200');
  const scrollY = useSharedValue(0);

  const internalScrollHandler = useAnimatedScrollHandler((event) => {
    scrollY.value = event.contentOffset.y;
  });

  const progressPercent = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;

  const moduleColorName = useMemo(() => getColorForModule(module), [module]);
  const moduleColorToken = `$${moduleColorName}500` as keyof gluestack.UIConfig['tokens']['colors'];

  // Immersive Hero Animations
  const heroStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [0, heroHeight - 100], [1, 0], Extrapolation.CLAMP),
    transform: [
      { scale: interpolate(scrollY.value, [-100, 0], [1.2, 1], Extrapolation.CLAMP) },
      { translateY: interpolate(scrollY.value, [0, heroHeight], [0, -heroHeight / 3], Extrapolation.CLAMP) },
    ] as ViewStyle['transform'],
  }));

  // Coordinated Header Animations
  const headerContainerStyle = useAnimatedStyle(() => {
    // Start appearing much earlier (when ~40% of hero is scrolled)
    const startAppearing = 20;
    const fullySticky = heroHeight - 80;

    return {
      backgroundColor: interpolateColor(
        scrollY.value,
        [startAppearing, fullySticky],
        ['rgba(255,255,255,0)', white],
      ),
      borderBottomColor: interpolateColor(
        scrollY.value,
        [startAppearing, fullySticky],
        ['rgba(255,255,255,0)', neutral200],
      ),
      borderBottomWidth: scrollY.value > startAppearing ? 1 : 0,
    };
  });

  const backButtonContainerStyle = useAnimatedStyle(() => {
    const startAppearing = 20;
    const fullySticky = heroHeight - 80;

    return {
      backgroundColor: interpolateColor(
        scrollY.value,
        [startAppearing, fullySticky],
        ['rgba(0,0,0,0.2)', 'rgba(255,255,255,0)'], // Darker overlay on image -> transparent on white
      ),
      borderColor: interpolateColor(
        scrollY.value,
        [startAppearing, fullySticky],
        ['rgba(255,255,255,0.3)', neutral200],
      ),
    };
  });

  const heroBackIconStyle = useAnimatedStyle(() => {
    const startAppearing = 40;
    const fullySticky = heroHeight - 100;
    return {
      opacity: interpolate(scrollY.value, [startAppearing, fullySticky], [1, 0], Extrapolation.CLAMP),
    };
  });

  const stickyBackIconStyle = useAnimatedStyle(() => {
    const startAppearing = 40;
    const fullySticky = heroHeight - 100;
    return {
      opacity: interpolate(scrollY.value, [startAppearing, fullySticky], [0, 1], Extrapolation.CLAMP),
    };
  });

  const stickyContentStyle = useAnimatedStyle(() => {
    const startAppearing = 60;
    const fullySticky = heroHeight - 60;

    return {
      opacity: interpolate(scrollY.value, [startAppearing, fullySticky], [0, 1], Extrapolation.CLAMP),
    };
  });

  const stickyProgressStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [heroHeight - 40, heroHeight - 20], [0, 1], Extrapolation.CLAMP),
  }));

  const ABSOLUTE_FILL = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    z: 0, // Need to make this a different length somehow? Let's just remove the style
  };

  const progressBarStyle = useAnimatedStyle(() => ({
    width: `${progressPercent}%`,
  }));

  const headerAnimatedStyle = useAnimatedStyle(() => ({
    paddingHorizontal: isMobile ? 16 : 32,
  }));

  return (
    <Box flex={1} bg="transparent">
      <Animated.ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={SCROLL_VIEW_CONTENT_STYLE}
        onScroll={internalScrollHandler}
        scrollEventThrottle={16}
      >
        {/* 2. Redesigned Immersive Hero */}
        <AnimatedBox
          style={[heroStyle, { height: heroHeight }]}
          w="100%"
          overflow="hidden"
          position="relative"
        >
          <Image
            source={module?.imageUrl || FALLBACK_IMAGE}
            style={ABSOLUTE_FILL}
            contentFit="cover"
          />
          <LinearGradient
            colors={['rgba(0,0,0,0.7)', 'rgba(0,0,0,0.3)', white]}
            style={ABSOLUTE_FILL}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
          />

          <VStack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            justifyContent="flex-end"
            pb={60}
            px={40}
            alignItems="center"
          >
            {/* Title & Progress Unified Block */}
            <VStack space="lg" alignItems="center" alignSelf="center">
              <VStack space="xs" alignItems="center">
                <Heading
                  size="4xl"
                  color="white"
                  textAlign="center"
                  sx={{
                    fontWeight: '900',
                  }}
                >
                  {module?.title}
                </Heading>
              </VStack>

              {/* Integrated Progress - Minimalist Bar */}
              <Box width="100%" maxWidth={300} h={4} bg="rgba(255,255,255,0.15)" borderRadius="$full" overflow="hidden">
                <Box w={`${progressPercent}%`} h="100%" bg={moduleColorToken || '$brandBase'} borderRadius="$full" />
              </Box>
            </VStack>
          </VStack>
        </AnimatedBox>

        <Box maxWidth={720} alignSelf="center" width="100%" bg="$bgApp" minHeight="100%">
          <PacingHeader pacing={pacing} onWeeksChange={onUpdateWeeks} targetWeeks={targetWeeks} />

          <VStack pt="$8">
            {levelGroups.map((levelGroup) => (
              <VStack key={levelGroup.level.id} mb="$16">
                {/* Level Header - Typography Led */}
                <VStack px="$4" mb="$4" space="xs">
                  <Text size="2xs" fontWeight="$black" color="$textHint" textTransform="uppercase" letterSpacing={2}>
                    LEVEL
                    {' '}
                    {levelGroup.level.id}
                  </Text>
                  <Heading size="xl" color="$textHeading">
                    {levelGroup.level.title || `Segment ${levelGroup.level.id}`}
                  </Heading>
                </VStack>

                <VStack px="$4">
                  {levelGroup.weeks.map((week, weekIdx) => (
                    <VStack
                      key={`l${levelGroup.level.id}-w${week.weekNum}`}
                      mb={weekIdx === levelGroup.weeks.length - 1 ? 0 : isPacingDisabled ? '$4' : '$10'}
                    >
                      {!isPacingDisabled && (
                        <HStack alignItems="center" space="md" mb="$4">
                          <Box w={32} alignItems="center">
                            <Box w={4} h={16} borderRadius="$full" bg="$neutral300" />
                          </Box>
                          <Text
                            size="xs"
                            fontWeight="$black"
                            color="$textSubtle"
                            textTransform="uppercase"
                            letterSpacing={1}
                          >
                            Week
                            {' '}
                            {week.weekNum}
                          </Text>
                        </HStack>
                      )}

                      <Box position="relative">
                        {!isPacingDisabled && (
                          <Box position="absolute" left={15} top={0} bottom={0} w={2} bg="$borderLight" opacity={0.4} />
                        )}
                        <VStack space="sm">
                          {week.tasks.map((task) => (
                            <TimelineTask
                              key={task.id}
                              testID={`timeline-task-${task.id}`}
                              task={task}
                              status={getTaskStatus(task, task.id === firstIncompleteId)}
                              isSelected={selectedTaskId === task.id}
                              allTasks={effectiveAllTasks}
                              levelOrder={levelOrder}
                              onPress={() => onSelectTask(task)}
                            />
                          ))}
                        </VStack>
                      </Box>
                    </VStack>
                  ))}
                </VStack>
              </VStack>
            ))}

            {/* Refined Module Progress Footer */}
            <VStack space="lg" px="$6" py="$12" mt="$8" borderTopWidth={1} borderColor="$borderLight" mx="$4">
              <HStack alignItems="center" justifyContent="space-between">
                <VStack space="xs">
                  <HStack space="sm" alignItems="center">
                    <Box w={3} h={12} borderRadius={2} bg={allTasksCompleted ? '$statusSuccess' : '$brandBase'} />
                    <Text
                      size="xs"
                      fontWeight="$black"
                      color={allTasksCompleted ? '$statusSuccess' : '$textSubtle'}
                      textTransform="uppercase"
                      letterSpacing={1}
                    >
                      {allTasksCompleted ? 'Journey Complete' : 'Current Target'}
                    </Text>
                  </HStack>
                  <Heading size="lg" color="$textHeading">
                    {allTasksCompleted
                      ? 'Ready to finalize your progress?'
                      : pacing?.targetDate.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      }) || 'No target date'}
                  </Heading>
                </VStack>

                {allTasksCompleted ? (
                  <Button
                    size="md"
                    bg="$actionPrimary"
                    onPress={onConfirmModule}
                    isDisabled={isSubmitting}
                    borderRadius="$full"
                    px="$6"
                  >
                    <HStack space="xs" alignItems="center">
                      {isSubmitting ? (
                        <Spinner size="small" color="white" />
                      ) : (
                        <ButtonText fontWeight="bold">Submit Module</ButtonText>
                      )}
                    </HStack>
                  </Button>
                ) : (
                  <Box p="$3" bg="$bgSubtle" borderRadius="$full" borderWidth={1} borderColor="$borderLight">
                    <Icon as={Target} size="md" color="$brandBase" />
                  </Box>
                )}
              </HStack>
            </VStack>
          </VStack>
        </Box>
      </Animated.ScrollView>

      {/* 1. Integrated Header (Handles both Hero and Sticky states) */}
      <AnimatedBox
        pointerEvents="box-none"
        position="absolute"
        top={0}
        left={0}
        right={0}
        height={64}
        zIndex={40}
        style={[
          headerContainerStyle,
          headerAnimatedStyle,
        ]}
      >
        <HStack h="100%" alignItems="center" space="md" pointerEvents="box-none">
          <Pressable onPress={onBack}>
            <Box
              w={38}
              h={38}
              borderRadius="$full"
              borderWidth={1}
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              position="relative"
            >
              <AnimatedBox
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                style={backButtonContainerStyle}
              />
              {Platform.OS !== 'web' && (
                <AnimatedBox
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  style={heroBackIconStyle}
                >
                  <BlurView
                    intensity={30}
                    style={ABSOLUTE_FILL}
                    tint="dark"
                  />
                </AnimatedBox>
              )}
              <AnimatedBox
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                alignItems="center"
                justifyContent="center"
                style={heroBackIconStyle}
              >
                <Icon as={ArrowLeft} size="lg" color="white" />
              </AnimatedBox>
              <AnimatedBox
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                alignItems="center"
                justifyContent="center"
                style={stickyBackIconStyle}
              >
                <Icon as={ArrowLeft} size="lg" color="$textBody" />
              </AnimatedBox>
            </Box>
          </Pressable>

          {/* Sticky Content - Only emerges on scroll */}
          <AnimatedBox
            pointerEvents="box-none"
            flex={1}
            style={stickyContentStyle}
          >
            <HStack alignItems="center" pointerEvents="box-none">
              {/* Identity Accent Bar - Replaces noisy thumbnail */}
              <Box w={4} h={24} bg={moduleColorToken} borderRadius="$full" mr="$3" />
              <VStack flex={1} pointerEvents="box-none">
                <Heading size="sm" color="$textHeading" numberOfLines={1}>
                  {module?.title}
                </Heading>
              </VStack>
            </HStack>
          </AnimatedBox>

          {/* Right side - reserved for future header actions */}
          <HStack alignItems="center" space="md" />
        </HStack>

        {/* Progress Bar in Sticky Header */}
        <AnimatedBox
          style={stickyProgressStyle}
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          height={2}
          bg="$bgSubtle"
          opacity={0}
        >
          <AnimatedBox style={progressBarStyle} bg={moduleColorToken || '$brandBase'} h="$full" />
        </AnimatedBox>
      </AnimatedBox>
    </Box>
  );
};

export default JourneyTimeline;
